import React from 'react';
import { 
  Container, Paper, Box, Typography, Button, IconButton, 
  Snackbar, Alert, CircularProgress, Divider, Tooltip, TextField
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ShareIcon from '@mui/icons-material/Share';
import { ThemeProvider } from '@mui/material/styles';

import theme from '../../../theme/theme';
import { DRAFT_KEYS } from '../../../utils/draftUtils';
import DraftSaveButton from '../../../components/DraftSaveButton';

import { usePostOpOrders } from './hooks/usePostOpOrders';
import LabOrdersSection from './screens/LabOrdersSection';
import SignaturesSection from './screens/SignaturesSection';

const PostOperativeOrders = () => {
  const { uhid_no } = useParams();
  const navigate = useNavigate();

  const {
    formData, setFormData,
    errors, 
    success,
    loading,
    submitStatus, setSubmitStatus,
    signatureDialogOpen,
    signaturePadRef,
    handleInputChange, handleBlur,
    handleSignatureOpen, handleSignatureClear, handleSignatureSave, handleSignatureClose,
    handleSubmit, handleShare,
    mutationLoading
  } = usePostOpOrders(uhid_no, navigate);

  const handleBack = () => {
    navigate(`/mainPage/${uhid_no}`, {
      state: { from: 'PostOperativePhase', status: 'success', step: 3 },
    });
  };

  const handleHomeNavigation = () => {
    navigate('/home');
  };

  const goToVideoRecorder = (formName, uhid) => {
    navigate(`/video?form=${encodeURIComponent(formName)}&uhid_no=${encodeURIComponent(uhid)}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: '16px' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h4" sx={{ color: '#008080', fontWeight: 700, letterSpacing: 1 }}>
                POST-OPERATIVE ORDERS
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img src="/logo.png" alt="Logo" style={{ width: 40, height: 40 }} />
              <Typography variant="h5" sx={{ color: '#008080', fontWeight: 700 }}>OTMS</Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Navigation Controls */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Button variant="outlined" onClick={handleBack} sx={{ borderRadius: '8px' }}>Back</Button>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button 
                variant="contained" 
                onClick={handleHomeNavigation}
                sx={{ borderRadius: '8px', bgcolor: '#008080', '&:hover': { bgcolor: '#006666' } }}
              >
                Home
              </Button>
              <Button 
                variant="contained" 
                onClick={() => goToVideoRecorder("POST-OPERATIVE ORDERS", uhid_no)}
                sx={{ borderRadius: '8px', bgcolor: '#008080', '&:hover': { bgcolor: '#006666' } }}
              >
                Add Video
              </Button>
              <Tooltip title="Share with Doctor">
                <IconButton onClick={handleShare} sx={{ color: '#008080', border: '1px solid #008080' }}>
                  <ShareIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 6 }}>
              <TextField
                fullWidth
                label="UHID No."
                value={formData.uhid_no}
                InputProps={{ readOnly: true }}
                variant="outlined"
              />
            </Box>

            <Box sx={{ mb: 6 }}>
              <LabOrdersSection 
                formData={formData}
                setFormData={setFormData}
              />
            </Box>

            <Box sx={{ mb: 6 }}>
              <Typography variant="h6" sx={{ color: '#008080', mb: 2, fontWeight: 600 }}>Order Date & Time</Typography>
              <Divider sx={{ mb: 3 }} />
              <TextField
                fullWidth
                type="datetime-local"
                label="Order Date & Time"
                name="order_datetime"
                required
                error={!!errors.order_datetime}
                helperText={errors.order_datetime}
                value={formData.order_datetime || ""}
                onChange={handleInputChange}
                onBlur={handleBlur}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: new Date().toISOString().slice(0, 16) }}
              />
            </Box>

            <Box sx={{ mb: 6 }}>
              <SignaturesSection 
                formData={formData}
                errors={errors}
                handleInputChange={handleInputChange}
                handleBlur={handleBlur}
                signatureDialogOpen={signatureDialogOpen}
                signaturePadRef={signaturePadRef}
                handleSignatureOpen={handleSignatureOpen}
                handleSignatureClear={handleSignatureClear}
                handleSignatureSave={handleSignatureSave}
                handleSignatureClose={handleSignatureClose}
              />
            </Box>

            {/* Form Actions */}
            <Box sx={{ display: 'flex', gap: 2, mt: 6 }}>
              <DraftSaveButton
                formKey={DRAFT_KEYS.POST_OPERATIVE_ORDERS}
                uhidNo={uhid_no}
                formData={formData}
                onDraftLoaded={(draftData) => setFormData(prev => ({ ...prev, ...draftData }))}
                variant="outlined"
                size="large"
                isUpdate={success}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={mutationLoading}
                startIcon={mutationLoading ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{ 
                  borderRadius: '10px', 
                  bgcolor: '#008080', 
                  py: 1.5,
                  '&:hover': { bgcolor: '#006666' } 
                }}
              >
                {mutationLoading ? 'Submitting...' : (success ? 'Update Orders' : 'Submit Orders')}
              </Button>
            </Box>
          </form>

          {/* Feedback SnackBar */}
          <Snackbar 
            open={submitStatus.open} 
            autoHideDuration={6000} 
            onClose={() => setSubmitStatus(prev => ({ ...prev, open: false }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert 
              onClose={() => setSubmitStatus(prev => ({ ...prev, open: false }))} 
              severity={submitStatus.severity} 
              sx={{ width: '100%', borderRadius: '8px' }}
            >
              {submitStatus.message}
            </Alert>
          </Snackbar>

          {success && (
            <Alert severity="info" sx={{ mt: 3, borderRadius: '8px' }}>
              You are updating an existing order record for this patient.
            </Alert>
          )}
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default PostOperativeOrders;
